import React, {useState} from 'react'
import ReactDOM from 'react-dom'
import Skeleton from 'react-loading-skeleton'

const FONT = "'Open Sans', sans-serif"

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  box: {
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    border: '1px solid #e2e8f0',
    width: '95vw',
    maxWidth: '1100px',
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    fontFamily: FONT,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px 16px',
    borderBottom: '1px solid #e2e8f0',
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 700,
    color: '#1e293b',
    fontFamily: FONT,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    padding: '0',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#64748b',
    borderRadius: '4px',
  },
  body: {
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '20px 24px',
    flex: 1,
  },
  sectionLabel: {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#64748b',
    fontFamily: FONT,
    margin: '0 0 8px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #e2e8f0',
    gap: '16px',
  },
  rowLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1e293b',
    fontFamily: FONT,
    flexShrink: 0,
  },
  rowValue: {
    fontSize: '13px',
    color: '#64748b',
    fontFamily: FONT,
    textAlign: 'right',
    wordBreak: 'break-all',
    overflowWrap: 'anywhere',
    minWidth: 0,
    userSelect: 'text',
  },
  link: {
    color: '#26325f',
    textDecoration: 'none',
    fontWeight: 500,
  },
  triggerBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    borderRadius: '999px',
    border: '1px solid #e2e8f0',
    background: '#f4f6fb',
    fontSize: '12px',
    fontWeight: 500,
    fontFamily: FONT,
    color: '#1e293b',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
}

const LINKS = ['Sales Rep Email', 'Email', 'Website', 'Google Map']
const DISCOVERY_FIELDS = [
  'Business Opportunity',
  'Current Software Stack',
  'Timeline & Buying Intent',
]
const NOTES_EXCLUDED_TYPES = [
  'onboarding',
  'creative consultation',
  'creative',
  'thryv-success-services',
  'thryv success services',
]

const formatDiscoveryAnswer = answer => {
  if (!answer || answer === '-') return '-'
  const items = answer
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '8px 0',
      }}
    >
      {items.map((item, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            padding: '8px 12px',
            backgroundColor: '#f8fafc',
            borderRadius: '6px',
            borderLeft: '3px solid #26325f',
          }}
        >
          <span
            style={{
              marginRight: '10px',
              flexShrink: 0,
              fontWeight: 700,
              color: '#26325f',
              fontSize: '14px',
            }}
          >
            •
          </span>
          <span
            style={{
              flex: 1,
              wordBreak: 'break-word',
              color: '#1e293b',
              fontSize: '13px',
              fontFamily: FONT,
            }}
          >
            {item}
          </span>
        </div>
      ))}
    </div>
  )
}

const DetailsModal = ({
  getData,
  buttonName,
  title,
  onClose,
  meeting_type,
  height,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)

  const open = async () => {
    setIsOpen(true)
    setLoading(true)
    try {
      const fetched = await getData()
      setData(fetched)
    } finally {
      setLoading(false)
    }
  }

  const close = () => {
    setIsOpen(false)
    setData([])
    if (onClose) onClose()
  }

  const modal = isOpen ? (
    <div style={styles.overlay} onClick={close}>
      <div style={styles.box} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>{title}</h3>
          <button
            type="button"
            style={styles.closeBtn}
            onClick={close}
            aria-label="Close"
          >
            <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1L13 13M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div style={styles.body}>
          <style>{`
            .dm-body a { color: #26325f !important; text-decoration: none; font-weight: 500; }
            .dm-body a:hover { color: #3b4fd8 !important; text-decoration: underline; }
          `}</style>
          {loading && <Skeleton height={height || '300px'} />}
          {!loading &&
            data.map(({sectionType, sectionName, sectionData, Component}, i) =>
              sectionName === 'Notes' &&
              NOTES_EXCLUDED_TYPES.includes(meeting_type) ? null : (
                <div
                  key={i}
                  className="dm-body"
                  style={{
                    marginBottom: sectionName ? '20px' : '8px',
                    marginTop: i > 0 && sectionName ? '12px' : 0,
                  }}
                >
                  {sectionName && (
                    <p style={styles.sectionLabel}>{sectionName}</p>
                  )}
                  {sectionType === 'object' &&
                    sectionData &&
                    Object.keys(sectionData).map((prop, j) =>
                      sectionData[prop] === '-' ? null : (
                        <div key={j} style={styles.row}>
                          <span style={styles.rowLabel}>{prop}</span>
                          <span style={styles.rowValue}>
                            {LINKS.includes(prop) ? (
                              <>
                                {(prop === 'Sales Rep Email' ||
                                  prop === 'Email') && (
                                  <a
                                    href={`mailto:${sectionData[prop]}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={styles.link}
                                  >
                                    {sectionData[prop]}
                                  </a>
                                )}
                                {prop === 'Google Map' && (
                                  <a
                                    href={sectionData[prop]}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={styles.link}
                                  >
                                    View Location
                                  </a>
                                )}
                                {prop === 'Website' && (
                                  <a
                                    href={sectionData[prop]}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={styles.link}
                                  >
                                    {sectionData[prop]}
                                  </a>
                                )}
                              </>
                            ) : DISCOVERY_FIELDS.includes(prop) ? (
                              formatDiscoveryAnswer(sectionData[prop])
                            ) : (
                              sectionData[prop]
                            )}
                          </span>
                        </div>
                      ),
                    )}
                  {sectionType === 'custom' && Component && <Component />}
                </div>
              ),
            )}
        </div>
      </div>
    </div>
  ) : null

  return (
    <>
      <button type="button" style={styles.triggerBtn} onClick={open}>
        {buttonName}
      </button>
      {ReactDOM.createPortal(modal, document.body)}
    </>
  )
}

export {DetailsModal}
